import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Checkbox, CheckedState } from '@ge/components/checkbox';
import { Icon, Icons } from '@ge/components/icon';
import { Text } from '@ge/components/typography';
import { TaskTemplateModes, DefaultTime, TaskFlag } from '@ge/models/constants';
import { globalColors, typography } from '@ge/tokens';

import { addUTCOffset } from '../../../../../util';
import {
  NumberMetaField,
  SpanMetaField,
  TimeMetaField,
  ToggleButtonMetaField,
  TimepickerStrMetaField,
} from '../../../../meta-fields';
import { DateMetaField } from '../../../../meta-fields/date-meta-field';
import { TimeStrMetaField } from '../../../../meta-fields/time-str-meta-field';
import { TaskContext } from '../../../task-context';
import { AssigneeSelect } from '../../../task-fields/assignee-select';
import {
  ColumnTitle,
  ColumnDetail,
  FieldGroup,
  StyledDateContainer,
  FormRow,
  DisplayField,
  DisplayLabel,
} from '../../../task-template-shared';

import { ScheduleErrors } from './schedule-errors';

const StyledFieldGroup = styled(FieldGroup)`
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
`;

const StyledScheduleErrors = styled(ScheduleErrors)`
  margin-top: 8px;
`;

const StyledMultiDayScheduleErrors = styled(ScheduleErrors)`
  margin: 8px 0 10px 12px;
`;

dayjs.extend(duration);

const StyledFormRow = styled(FormRow)`
  margin-bottom: 0;
  justify-content: flex-start;
  margin: 8px 0 0 12px;
  .select__control {
    width: 120px;
  }
  .select__dropdown-control {
    width: 120px;
  }
  .select__dropdown-control {
    width: 120px !important;
  }
`;

const IndividualStyledFormRow = styled(StyledFormRow)`
  margin: 13px 0 0 12px;
`;

//styling changes to be added
const StyledAssigneeContainer = styled.div`
  position: relative;
`;

const StyledDurationContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const DeleteMultiDayContainer = styled.div`
  position: absolute;
  margin-top: -6px;
  left: 90%;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 8px 0 0 12px;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 15px;
  color: ${(props) => props.theme.button.textColor}; !important;
`;

const MultiDayContainer = styled.div`
  background: #2f3841;
  height: 100%;
  width: 95%;
  margin-top: 15px;
  margin-bottom: 10px;
  padding: 10px 0 10px 0;
  border-radius: 4px 4px 4px 4px;
  margin-left: 0.5rem;
`;

const MultidayDivider = styled.hr`
  box-sizing: border-box;
  border: 1px solid ${(props) => props.theme.malAdjustmentTable.malStatusModal.tableDividerColor};
  margin-top: 10px;
  margin-bottom: 10px;
`;

const StyledNumberMetaField = styled(NumberMetaField)`
  color: ${(props) => props.theme.entityDetails.tasks.details.inputColor};
  width: 45px;
  height: 25px;
`;

const IndividualdayDateContainer = styled(StyledDateContainer)`
  margin-right: 13px;
`;

const AddButton = styled.button`
  color: ${(props) => props.theme.entityDetails.notes.addNote};
  display: flex;
  font-size: 11px;
  font-weight: ${typography.weight.medium};
  line-height: 13px;
  margin: 0 16px 15px auto;
  text-transform: uppercase;
`;

const RangeButton = styled.button`
  color: ${(props) => props.theme.entityDetails.notes.addNote};
  display: flex;
  font-size: 11px;
  font-weight: 600;
  line-height: 13px;
  text-transform: uppercase;
  align-items: center;
  margin-top: 15px;
  padding-right: 0px;
  padding-left: 0px;
    &:disabled {
    cursor: not-allowed;
    color: ${(props) => props.theme.colors.grey4};
`;

export const NumberFieldAlignment = styled.div`
  display: flex;
  flex-direction: column;
`;
const IndividualNumberField = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 13px;
  margin-top: 5px;
`;

const StyledIcon = styled(Icon).attrs((props) => ({
  color: props.theme.manage.taskItem.iconColor,
}))`
  margin-bottom: 8px;
`;

const AddMultidayIcon = styled(Icon).attrs((props) => ({
  size: 10,
  icon: Icons.ADD,
  color: props.theme.entityDetails.notes.addNote,
}))`
  flex-shrink: 0;
  margin: 1px 7px 0;
`;

const ToggleContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  margin-left: 12px;
  gap: 6px;
  align-items: center;

  span {
    padding: 3px 5px 3px 0px;
    font-size: 12px;
    letter-spacing: 0.5px;
    color: ${(props) => props.theme.colors.grey4};
  }

  button {
    height: 16px;
    width: 32px;
    div {
      height: 10px;
      width: 10px;
    }
  }
`;

const StyledIndividualDate = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-right: 13px;
  span {
    font-size: 12px;
    letter-spacing: 0.5px;
    color: ${(props) => props.theme.colors.grey5};
  }
`;

const IndividualLabelContainer = styled.div`
  display: flex;
  gap: 48px;
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  color: ${(props) => props.theme.dangerColor} !important;
  display: block;
  margin-bottom: 10px;
  width: 100%;
  margin-top: 5px;
  margin-left: 12px;
`;

const ErrorIcon = styled(Icon).attrs(() => ({
  size: 8,
  icon: Icons.DATA_NETCOMM,
  color: `${globalColors.red2} !important`,
}))`
  margin-right: 2px;
`;

export const EditSchedule = ({ metadata, rules, task, templateMode }) => {
  const { t } = useTranslation(['tasks'], { useSuspense: false });
  const { approvalFlag } = useStoreState((state) => state.tenant.featureFlags);
  const { enabledApprovalFlag } = useStoreState((state) => state.tasks);

  const { readOnly, required } = metadata.scheduleDate.edit;
  const scheduleDate = task?.scheduleDateTz || null;
  const [multiDay, setMultiDay] = useState(CheckedState.UNCHECKED);
  const { watch, register, setValue, unregister } = useFormContext();
  const watchScheduleDate = watch('scheduleDate');

  const watchDueDate = watch('dueDate');
  const watchEligibleStartDate = watch('eligibleStartDate');

  const watchMultiDay = watch('schedule', []);

  const {
    taskState: { timezone },
  } = useContext(TaskContext);

  const { fields, append, remove } = useFieldArray({
    name: 'schedule',
  });

  const defaultMultiDayFields = useMemo(
    () => ({
      scheduleStartDate: '',
      scheduleEndDate: '',
      crewIds: '',
      dailyStartTime: '',
      estimatedDailyDurationHours: metadata.estimatedDailyDurationHours?.defaultSelection || '',
      estimatedDailyDurationMinutes: metadata.estimatedDailyDurationMinutes?.defaultSelection || '',
      isIndividualDay: false,
      individualDays: [],
    }),
    [metadata],
  );

  const defaultIndividualDayFields = useMemo(
    () => ({
      date: '',
      crewIds: '',
      startTime: '',
      estimatedDailyDurationHours: metadata.estimatedDailyDurationHours?.defaultSelection || '',
      estimatedDailyDurationMinutes: metadata.estimatedDailyDurationMinutes?.defaultSelection || '',
    }),
    [metadata],
  );

  useEffect(() => {
    register('multiDay');
    if (task) {
      const multiDaySchedule = task.multiDaySchedule;
      setValue(
        'multiDay',
        multiDaySchedule?.isMultiDay ? CheckedState.CHECKED : CheckedState.UNCHECKED,
      );
      setMultiDay(multiDaySchedule?.isMultiDay ? CheckedState.CHECKED : CheckedState.UNCHECKED);
      multiDaySchedule?.schedules?.forEach((s) => {
        const curr = {
          ...s,
          scheduleStartDate: addUTCOffset(s.scheduleStartDate, timezone),
          scheduleEndDate: addUTCOffset(s.scheduleEndDate, timezone),
          dailyStartTime: addUTCOffset(s.dailyStartTime),
          individualDays: (s.individualDays || []).map((i) => {
            return {
              ...i,
              date: addUTCOffset(i.date, timezone),
              startTime: addUTCOffset(i.startTime),
            };
          }),
        };
        append(curr);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    if (multiDay === CheckedState.CHECKED && fields.length === 0) {
      append({ ...defaultMultiDayFields });
    }
  }, [append, defaultMultiDayFields, fields.length, multiDay]);

  const handleRemove = useCallback(
    (index) => {
      // Only clear field array values if there is one row, otherwise delete row
      if (watchMultiDay?.length === 1) {
        // The reset functionality has some issues with Watch and fieldarray states of the react-hook-form(V6.15.8)
        // So need to reset twice to get this working (react-hook-form - Version:6.15.8)
        setValue(`schedule`, [{ ...defaultMultiDayFields }]);
        setValue(`schedule`, [{ ...defaultMultiDayFields }]);
      } else {
        remove(index);
      }
    },
    [watchMultiDay, setValue, defaultMultiDayFields, remove],
  );

  const toggleMultiDayTask = useCallback(() => {
    const updatedValue =
      multiDay === CheckedState.CHECKED ? CheckedState.UNCHECKED : CheckedState.CHECKED;
    setMultiDay(updatedValue);
    setValue('multiDay', updatedValue);
  }, [multiDay, setValue]);

  const applyRangeForIndividual = useCallback(
    (index) => {
      const currentSchedule = watchMultiDay[index];
      const {
        scheduleStartDate,
        scheduleEndDate,
        crewIds,
        dailyStartTime,
        estimatedDailyDurationHours,
        estimatedDailyDurationMinutes,
        individualDays,
      } = currentSchedule;

      if (scheduleStartDate && scheduleEndDate) {
        const dayRange = dayjs(scheduleEndDate).diff(scheduleStartDate, 'days', false) + 1;

        unregister(`schedule[${index}].individualDays`);
        register(`schedule[${index}].individualDays`);
        setValue(`schedule[${index}].individualDays`, null);
        setTimeout(() => {
          setValue(
            `schedule[${index}].individualDays`,
            new Array(dayRange).fill({ ...defaultIndividualDayFields }).map((x, i) => {
              const individualDate = dayjs(scheduleStartDate).add(i, 'day');
              const prevDataEnteredByUser =
                individualDays?.find(
                  (individualDay) => individualDay.date === individualDate.format('DD MMM YY'),
                ) || {};
              return {
                ...x,
                ...prevDataEnteredByUser,
                date: individualDate,
                crewIds: crewIds || prevDataEnteredByUser.crewIds,
                startTime:
                  dailyStartTime?.utcOffset(timezone, true) || prevDataEnteredByUser.startTime,
                estimatedDailyDurationHours:
                  estimatedDailyDurationHours || prevDataEnteredByUser.estimatedDailyDurationHours,
                estimatedDailyDurationMinutes:
                  estimatedDailyDurationMinutes ||
                  prevDataEnteredByUser.estimatedDailyDurationMinutes,
              };
            }),
          );
        }, 100);
      }
    },
    [defaultIndividualDayFields, register, setValue, timezone, unregister, watchMultiDay],
  );

  const toggleButtonHandler = useCallback(
    (e, index) => {
      e.preventDefault();
      e.stopPropagation();
      const currentSchedule = watchMultiDay[index];
      const { scheduleStartDate, scheduleEndDate } = currentSchedule;
      const newValue = !watchMultiDay[index]?.isIndividualDay;
      if (scheduleStartDate && scheduleEndDate) {
        if (newValue) {
          applyRangeForIndividual(index);
        }
      } else {
        setValue(`schedule[${index}].isIndividualDay`, false);
      }
    },
    [applyRangeForIndividual, setValue, watchMultiDay],
  );

  return (
    <div className="task-panel-row">
      <ColumnTitle>
        <h4>{t('section_header.schedule', 'Schedule')}</h4>
      </ColumnTitle>
      <ColumnDetail className="column-detail">
        <StyledFieldGroup>
          {!metadata?.multiDay.hidden && (
            <StyledCheckbox
              label={t('form.multi_day', 'Multi day')}
              checkState={multiDay}
              onChange={toggleMultiDayTask}
              name="multiDay"
            />
          )}
          {multiDay === CheckedState.UNCHECKED && (
            <StyledFormRow>
              {!metadata?.scheduleDate.hidden && (
                <StyledDateContainer>
                  <div className="label">{t('form.schedule_date', 'Schedule Start Date')}</div>
                  <DateMetaField
                    defaultValue={scheduleDate}
                    defaultSelection={metadata.scheduleDate?.defaultSelection}
                    metadata={{
                      readOnly:
                        readOnly ||
                        (approvalFlag && task?.flag !== TaskFlag.APPROVED && enabledApprovalFlag),
                      required,
                    }}
                    name="scheduleDate"
                    rules={rules}
                    validators={metadata.scheduleDate.edit?.validators}
                  />
                </StyledDateContainer>
              )}
              {!metadata?.startTime.hidden && (
                <StyledDateContainer>
                  <div className="label">{t('form.start_time', 'Daily Start Time')}</div>
                  <TimepickerStrMetaField
                    defaultValue={
                      task?.startTime
                        ? dayjs(task?.scheduleDate)
                            .set('hour', task.startTime.split(':')[0])
                            .set('minute', task.startTime.split(':')[1])
                        : null
                    }
                    metadata={{
                      readOnly:
                        readOnly ||
                        (approvalFlag && task?.flag !== TaskFlag.APPROVED && enabledApprovalFlag),
                      required,
                    }}
                    name="startTime"
                    rules={{
                      required: watchScheduleDate ? true : false,
                    }}
                  />
                </StyledDateContainer>
              )}
              {!metadata?.crewIds.hidden && (
                <StyledAssigneeContainer>
                  <AssigneeSelect
                    defaultValue={task?.crewIds?.length ? task?.crewIds[0] : null}
                    metadata={metadata?.crewIds.create}
                    task={task}
                    templateMode={templateMode}
                    disabled={
                      approvalFlag && task?.flag !== TaskFlag.APPROVED && enabledApprovalFlag
                    }
                  />
                </StyledAssigneeContainer>
              )}
            </StyledFormRow>
          )}
          {multiDay === CheckedState.CHECKED && (
            <div>
              <div>
                {fields?.map((multiDay, index) => {
                  const individualDays = watchMultiDay[index]?.individualDays;
                  const dayRange =
                    dayjs(watchMultiDay[index]?.scheduleEndDate).diff(
                      watchMultiDay[index]?.scheduleStartDate,
                      'days',
                      false,
                    ) + 1;
                  return (
                    <MultiDayContainer key={multiDay?.id}>
                      <StyledFormRow>
                        {!metadata?.scheduleStartDate.hidden && (
                          <StyledDateContainer>
                            <div className="label">
                              {t('form.schedule_start_date', 'Schedule Start Date')}
                            </div>
                            <DateMetaField
                              defaultSelection={metadata.scheduleStartDate?.defaultSelection}
                              defaultValue={multiDay.scheduleStartDate}
                              fieldArray={{
                                fieldName: 'schedule',
                                errorIndex: index,
                                errorName: 'scheduleStartDate',
                              }}
                              name={`schedule[${index}].scheduleStartDate`}
                              metadata={{ ...metadata, required: true }}
                              validators={metadata.scheduleStartDate.create?.validators}
                              rules={{
                                validate: {
                                  isAfterCompletedDate: (value) => {
                                    const endDate = watchMultiDay[index]?.scheduleEndDate;
                                    if (endDate === null) return true;
                                    return dayjs(value).isSameOrBefore(endDate);
                                  },
                                  isBeforeEgilibleDate: (value) => {
                                    if (watchEligibleStartDate === null) return true;
                                    return dayjs(value).isAfter(watchEligibleStartDate);
                                  },
                                  isAfterDueDate: (value) => {
                                    if (watchDueDate === null) return true;
                                    return dayjs(value).isBefore(watchEligibleStartDate);
                                  },
                                },
                              }}
                            />
                          </StyledDateContainer>
                        )}
                        {!metadata?.scheduleEndDate.hidden && (
                          <StyledDateContainer>
                            <div className="label">
                              {t('form.schedule_end_date', 'Schedule End Date')}
                            </div>
                            <DateMetaField
                              defaultSelection={metadata.scheduleEndDate?.defaultSelection}
                              defaultValue={multiDay.scheduleEndDate}
                              fieldArray={{
                                fieldName: 'schedule',
                                errorIndex: index,
                                errorName: 'scheduleEndDate',
                              }}
                              name={`schedule[${index}].scheduleEndDate`}
                              metadata={{ ...metadata, required: true }}
                              //validators={metadata.scheduleEndDate.create?.validators}
                              validators={metadata.scheduleDate.create?.validators}
                            />
                          </StyledDateContainer>
                        )}
                        {watchMultiDay[index]?.isIndividualDay && (
                          <RangeButton
                            type="button"
                            onClick={() => applyRangeForIndividual(index)}
                            disabled={
                              dayjs(watchMultiDay[index]?.scheduleStartDate).isSame(
                                dayjs(watchMultiDay[index]?.individualDays?.[0]?.date),
                                'day',
                              ) &&
                              dayjs(watchMultiDay[index]?.scheduleEndDate).isSame(
                                dayjs(
                                  watchMultiDay[index]?.individualDays?.[
                                    watchMultiDay[index]?.individualDays?.length - 1
                                  ]?.date,
                                ),
                                'day',
                              )
                            }
                          >
                            {t('form.apply_range', 'APPLY RANGE')}
                          </RangeButton>
                        )}
                        <DeleteMultiDayContainer>
                          <button type="button" onClick={() => handleRemove(index)}>
                            <StyledIcon icon={Icons.TRASH} size={13} />
                          </button>
                        </DeleteMultiDayContainer>
                      </StyledFormRow>
                      {dayjs(watchMultiDay[index]?.scheduleStartDate).isAfter(
                        watchMultiDay[index]?.scheduleEndDate,
                      ) && (
                        <ErrorText>
                          <ErrorIcon />
                          {t(
                            'form.error_start_date',
                            'Schedule Start Date must be on/before Schedule End Date.',
                          )}
                        </ErrorText>
                      )}
                      {!watchMultiDay[index]?.isIndividualDay && (
                        <StyledFormRow>
                          {!metadata?.crewIds.hidden && (
                            <StyledAssigneeContainer>
                              <AssigneeSelect
                                metadata={metadata.crewIds.create}
                                templateMode={templateMode}
                                multiDayDate={watchMultiDay[index]?.scheduleStartDate}
                                index={index}
                                task={task}
                                defaultValue={watchMultiDay[index]?.crewIds}
                              />
                            </StyledAssigneeContainer>
                          )}
                          {!metadata?.dailyStartTime.hidden && (
                            <StyledDateContainer>
                              <div style={{ marginBottom: '3px' }} className="label">
                                {t('form.start_time', 'Daily Start Time')}
                              </div>
                              <TimepickerStrMetaField
                                // date={dayjs(watchMultiDay[index]?.scheduleStartDate) || ''}
                                defaultValue={
                                  watchMultiDay[index]?.dailyStartTime
                                    ? dayjs(watchMultiDay[index]?.dailyStartTime)
                                    : null
                                }
                                name={`schedule[${index}].dailyStartTime`}
                                metadata={metadata.dailyStartTime.create}
                                fieldArray={{
                                  fieldName: 'schedule',
                                  errorIndex: index,
                                  errorName: 'dailyStartTime',
                                }}
                                rules={{
                                  required: watchMultiDay[index]?.scheduleStartDate ? true : false,
                                }}
                              />
                            </StyledDateContainer>
                          )}
                          <NumberFieldAlignment>
                            <StyledNumberMetaField
                              defaultValue={
                                watchMultiDay[index]?.estimatedDailyDurationHours ||
                                metadata.estimatedDailyDurationHours?.defaultSelection
                              }
                              fieldArray={{
                                fieldName: 'schedule',
                                errorIndex: index,
                                errorName: 'estimatedDailyDurationHours',
                              }}
                              isInteger
                              label={t('form.multiday_est_hours', 'Est. Daily')}
                              max={metadata.estimatedDailyDurationHours?.max}
                              metadata={{ ...metadata, required: true }}
                              min={0}
                              name={`schedule[${index}].estimatedDailyDurationHours`}
                              placeholder="0"
                              rules={
                                !parseInt(watchMultiDay[index]?.estimatedDailyDurationHours) &&
                                !parseInt(watchMultiDay[index]?.estimatedDailyDurationMinutes)
                                  ? {
                                      required:
                                        metadata.estimatedDailyDurationHours?.create.required,
                                      max: metadata.estimatedDailyDurationHours?.max,
                                      min: 0,
                                    }
                                  : { max: metadata.estimatedDailyDurationHours?.max }
                              }
                            />
                          </NumberFieldAlignment>
                          <NumberFieldAlignment style={{ marginLeft: '5px' }}>
                            {!metadata?.estimatedDailyDurationMinutes.hidden && (
                              <StyledNumberMetaField
                                defaultValue={
                                  watchMultiDay[index]?.estimatedDailyDurationMinutes ||
                                  metadata.estimatedDailyDurationMinutes?.defaultSelection
                                }
                                fieldArray={{
                                  fieldName: 'schedule',
                                  errorIndex: index,
                                  errorName: 'estimatedDailyDurationMinutes',
                                }}
                                isInteger
                                label={t('form.multiday_est_minitues', 'Duration (HH:MM)')}
                                metadata={{ ...metadata, required: true }}
                                max={metadata.estimatedDailyDurationMinutes?.max}
                                min={metadata.estimatedDailyDurationMinutes?.min}
                                name={`schedule[${index}].estimatedDailyDurationMinutes`}
                                placeholder="0"
                                rules={
                                  !parseInt(watchMultiDay[index]?.estimatedDailyDurationHours) &&
                                  !parseInt(watchMultiDay[index]?.estimatedDailyDurationMinutes)
                                    ? {
                                        required:
                                          metadata.estimatedDailyDurationMinutes?.create.required,
                                        max: metadata.estimatedDailyDurationMinutes?.max,
                                        min: 0,
                                      }
                                    : { max: metadata.estimatedDailyDurationMinutes?.max }
                                }
                              />
                            )}
                          </NumberFieldAlignment>
                        </StyledFormRow>
                      )}
                      <MultidayDivider />
                      <ToggleContainer>
                        <ToggleButtonMetaField
                          defaultValue={watchMultiDay[index]?.isIndividualDay}
                          handleOnClick={(e) => toggleButtonHandler(e, index)}
                          noIcon={true}
                          name={`schedule[${index}].isIndividualDay`}
                          metadata={{ ...metadata, required: false }}
                          fieldArray={{
                            fieldName: 'schedule',
                            errorIndex: index,
                            errorName: 'isIndividualDay',
                          }}
                        />
                        <span style={{ paddingRight: '0px' }}>
                          {t('show_individual_days', 'Show individual days')}
                        </span>
                        {watchMultiDay[index]?.isIndividualDay && (
                          <span>
                            ({dayRange} {'days'})
                          </span>
                        )}
                      </ToggleContainer>
                      <StyledFormRow>
                        {watchMultiDay[index]?.isIndividualDay && (
                          <IndividualLabelContainer>
                            <DisplayLabel>{t('form.date', 'Date')}</DisplayLabel>
                            <DisplayLabel>
                              {t('form.individual_start_time', 'Start Time')}
                            </DisplayLabel>
                            <DisplayLabel style={{ marginLeft: '5px', marginRight: '-30px' }}>
                              {t('form.multiday_est', 'Est.Duration (HH:MM)*')}
                            </DisplayLabel>
                            <DisplayLabel>{t('form.individual_assignee', 'Assignee')}</DisplayLabel>
                          </IndividualLabelContainer>
                        )}
                      </StyledFormRow>
                      {watchMultiDay[index]?.isIndividualDay &&
                        individualDays?.map((individualDay, individualIndex) => (
                          <IndividualStyledFormRow
                            key={`${dayjs(individualDay.date).format(
                              'DD MMM YY',
                            )}-${individualIndex}`}
                          >
                            <StyledIndividualDate>
                              <DisplayField className="read-only">
                                <SpanMetaField
                                  key={`${dayjs(individualDay.date).format(
                                    'DD MMM YY',
                                  )}-${individualIndex}`}
                                  name={`schedule[${index}].individualDays[${individualIndex}].date`}
                                  defaultValue={dayjs(individualDay.date).format('DD MMM YY')}
                                  metadata={{ ...metadata, required: false }}
                                />
                              </DisplayField>
                            </StyledIndividualDate>
                            {!metadata?.dailyStartTime.hidden && (
                              <IndividualdayDateContainer>
                                <div style={{ marginBottom: '5px' }} />
                                <TimepickerStrMetaField
                                  // date={dayjs(watchMultiDay[index]?.scheduleStartDate) || ''}
                                  defaultValue={
                                    individualDay.startTime ? dayjs(individualDay.startTime) : null
                                  }
                                  name={`schedule[${index}].individualDays[${individualIndex}].startTime`}
                                  metadata={metadata.dailyStartTime.create}
                                  fieldArray={{
                                    fieldName: `schedule`,
                                    errorIndex: index,
                                    errorName: `individualDays[${individualIndex}].startTime`,
                                  }}
                                  rules={{
                                    required: true,
                                  }}
                                />
                              </IndividualdayDateContainer>
                            )}
                            {!metadata?.estimatedDailyDurationHours.hidden && (
                              <IndividualNumberField>
                                <StyledDurationContainer>
                                  <NumberFieldAlignment>
                                    <StyledNumberMetaField
                                      defaultValue={
                                        individualDay.estimatedDailyDurationHours ||
                                        metadata.estimatedDailyDurationHours?.defaultSelection
                                      }
                                      isInteger
                                      max={metadata.estimatedDailyDurationHours?.max}
                                      metadata={{ ...metadata, required: true }}
                                      min={0}
                                      name={`schedule[${index}].individualDays[${individualIndex}].estimatedDailyDurationHours`}
                                      placeholder="0"
                                      fieldArray={{
                                        fieldName: `schedule`,
                                        errorIndex: index,
                                        errorName: `individualDays[${individualIndex}].estimatedDailyDurationHours`,
                                      }}
                                      rules={
                                        !parseInt(
                                          watchMultiDay[index]?.isIndividualDay
                                            ?.estimatedDailyDurationHours,
                                        ) &&
                                        !parseInt(
                                          watchMultiDay[index]?.isIndividualDay
                                            ?.estimatedDailyDurationMinutes,
                                        )
                                          ? {
                                              required:
                                                metadata.estimatedDailyDurationHours?.create
                                                  .required,
                                              max: metadata.estimatedDailyDurationHours?.max,
                                              min: 0,
                                            }
                                          : { max: metadata.estimatedDailyDurationHours?.max }
                                      }
                                    />
                                  </NumberFieldAlignment>
                                  <NumberFieldAlignment style={{ marginLeft: '5px' }}>
                                    {!metadata?.estimatedDailyDurationMinutes.hidden && (
                                      <StyledNumberMetaField
                                        defaultValue={
                                          individualDay.estimatedDailyDurationMinutes ||
                                          metadata.estimatedDailyDurationMinutes?.defaultSelection
                                        }
                                        isInteger
                                        metadata={{ ...metadata, required: true }}
                                        max={metadata.estimatedDailyDurationMinutes?.max}
                                        min={metadata.estimatedDailyDurationMinutes?.min}
                                        name={`schedule[${index}].individualDays[${individualIndex}].estimatedDailyDurationMinutes`}
                                        placeholder="0"
                                        fieldArray={{
                                          fieldName: `schedule`,
                                          errorIndex: index,
                                          errorName: `individualDays[${individualIndex}].estimatedDailyDurationMinutes`,
                                        }}
                                        rules={
                                          !parseInt(
                                            watchMultiDay[index]?.isIndividualDay
                                              ?.estimatedDailyDurationHours,
                                          ) &&
                                          !parseInt(
                                            watchMultiDay[index]?.isIndividualDay
                                              ?.estimatedDailyDurationMinutes,
                                          )
                                            ? {
                                                required:
                                                  metadata.estimatedDailyDurationMinutes?.create
                                                    .required,
                                                max: metadata.estimatedDailyDurationMinutes?.max,
                                                min: 0,
                                              }
                                            : { max: metadata.estimatedDailyDurationMinutes?.max }
                                        }
                                      />
                                    )}
                                  </NumberFieldAlignment>
                                </StyledDurationContainer>
                              </IndividualNumberField>
                            )}
                            {!metadata?.crewIds.hidden && (
                              <StyledAssigneeContainer>
                                <AssigneeSelect
                                  style={{ marginBottom: '0px' }}
                                  metadata={metadata.crewIds.create}
                                  templateMode={templateMode}
                                  name={`schedule[${index}].individualDays[${individualIndex}].crewIds`}
                                  label={true}
                                  defaultValue={individualDay.crewIds}
                                  task={task}
                                  multiDayDate={watchMultiDay[index]?.scheduleStartDate}
                                />
                              </StyledAssigneeContainer>
                            )}
                          </IndividualStyledFormRow>
                        ))}
                      <StyledMultiDayScheduleErrors errorIndex={index} />
                    </MultiDayContainer>
                  );
                })}
              </div>
              <AddButton type="button" onClick={() => append({ ...defaultMultiDayFields })}>
                <AddMultidayIcon />
                {t('form.add_button', 'ADD')}
              </AddButton>
            </div>
          )}
        </StyledFieldGroup>
        <StyledScheduleErrors />
      </ColumnDetail>
    </div>
  );
};

EditSchedule.propTypes = {
  metadata: PropTypes.object.isRequired,
  rules: PropTypes.instanceOf(Object),
  task: PropTypes.object.isRequired,
  templateMode: PropTypes.oneOf(Object.values(TaskTemplateModes)).isRequired,
};

EditSchedule.defaultProps = {
  rules: null,
};
